import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { asyncScheduler, of } from "rxjs";
import { catchError, map, mergeMap, switchMap, throttleTime } from "rxjs/operators";

import { MessagingService } from "../../../services/api/messaging.service";
import { MessagingV2Service } from "../../../services/api/messaging-v2.service";
import { MessagingActions } from "../../actions/messaging.actions";
import { FailState } from "../../models/fail-state";

/**
 * Window for collapsing clustered reconcile triggers (reconnect, tab focus, network resume)
 * into a single pair of full-list fetches.
 */
const RECONCILE_THROTTLE_MS = 5_000;

@Injectable()
export class MessagingEffects {
	archiveConversations = createEffect(() =>
		this.actions.pipe(
			ofType(MessagingActions.conversation.archive),
			switchMap((action) =>
				this.messagingService.archiveConversations(action.conversations).pipe(
					map(() => {
						const props = { userType: action.userType, params: {} };
						return MessagingActions.conversation.archiveSuccess(props);
					}),
					catchError((error) =>
						of(MessagingActions.conversation.archiveFail(new FailState(error)))
					)
				)
			)
		)
	);

	createConversation = createEffect(() =>
		this.actions.pipe(
			ofType(MessagingActions.conversation.create),
			switchMap((action) =>
				this.messagingService.createConversation(action.conversation).pipe(
					map((create) => {
						const props = { ...create, params: {} };
						return MessagingActions.conversation.createSuccess(props);
					}),
					catchError((error) =>
						of(MessagingActions.conversation.createFail(new FailState(error)))
					)
				)
			)
		)
	);

	editConversation = createEffect(() =>
		this.actions.pipe(
			ofType(MessagingActions.conversation.edit),
			switchMap((action) =>
				this.messagingV2Service.editConversation(action.conversation).pipe(
					map((edit) => {
						const props = {
							...edit,
							params: { id: [action.conversation.conversationId] }
						};
						return MessagingActions.conversation.editSuccess(props);
					}),
					catchError((error) =>
						of(MessagingActions.conversation.editFail(new FailState(error)))
					)
				)
			)
		)
	);

	createMessage = createEffect(() =>
		this.actions.pipe(
			ofType(MessagingActions.message.create),
			switchMap((action) =>
				this.messagingService.createMessage(action.message).pipe(
					switchMap((create) => {
						const props = {
							...create,
							params: {
								conversationId: [action.message.conversationId],
								sort: "createdAt,ASC"
							}
						};
						return [MessagingActions.message.createSuccess(props)];
					}),
					catchError((error) =>
						of(MessagingActions.message.createFail(new FailState(error)))
					)
				)
			)
		)
	);

	conversations = createEffect(() =>
		this.actions.pipe(
			ofType(
				MessagingActions.conversation.get,
				MessagingActions.conversation.createSuccess,
				MessagingActions.conversation.editSuccess,
				MessagingActions.conversation.archiveSuccess,
				MessagingActions.conversation.readSuccess
			),
			switchMap((action) => {
				const params = action.params ?? {};
				return this.messagingService.conversations(params).pipe(
					map((conversations) => {
						return MessagingActions.conversation.getSuccess({
							conversations: conversations || []
						});
					}),
					catchError((error) =>
						of(MessagingActions.conversation.getFail(new FailState(error)))
					)
				);
			})
		)
	);

	messages = createEffect(() =>
		this.actions.pipe(
			ofType(MessagingActions.message.get, MessagingActions.message.createSuccess),
			switchMap((action) =>
				this.messagingV2Service.messages(action.params).pipe(
					map((messages) => MessagingActions.message.getSuccess({ messages })),
					catchError((error) =>
						of(MessagingActions.message.getFail(new FailState(error)))
					)
				)
			)
		)
	);

	readMessages = createEffect(() =>
		this.actions.pipe(
			ofType(MessagingActions.conversation.read),
			switchMap((action) =>
				this.messagingService.markConversationMessagesAsRead(action.conversationId).pipe(
					map(() => {
						return MessagingActions.conversation.readSuccess({
							userType: action.userType,
							id: action.conversationId,
							params: { id: [action.conversationId] }
						});
					}),
					catchError((error) =>
						of(MessagingActions.conversation.readFail(new FailState(error)))
					)
				)
			)
		)
	);

	unreadMessages = createEffect(() =>
		this.actions.pipe(
			ofType(MessagingActions.conversation.unread),
			switchMap((action) =>
				this.messagingService.markConversationMessagesAsUnread(action.conversationId).pipe(
					map(() => {
						return MessagingActions.conversation.unreadSuccess({
							id: action.conversationId
						});
					}),
					catchError((error) =>
						of(MessagingActions.conversation.unreadFail(new FailState(error)))
					)
				)
			)
		)
	);

	/**
	 * A pushed message is already applied to the message list by the reducer, so the only stale
	 * data left is that one conversation's server-computed metadata (unread count, ordering).
	 * Fetch just that conversation — re-fetching the entire list per message was one full
	 * request per message on a busy thread.
	 *
	 * `mergeMap`, not `switchMap`: these fetches are for different conversations and must not
	 * cancel each other.
	 */
	wsMessageReceived$ = createEffect(() =>
		this.actions.pipe(
			ofType(MessagingActions.message.wsReceived),
			mergeMap((action) =>
				this.messagingService
					.conversations({ id: [action.message.conversationId] })
					.pipe(
						map((conversations) =>
							MessagingActions.conversation.getSuccess({
								conversations: conversations || []
							})
						),
						catchError((error) =>
							of(MessagingActions.conversation.getFail(new FailState(error)))
						)
					)
			)
		)
	);

	/**
	 * Full resync after a gap in the push channel — socket (re)connect, tab focus, or the
	 * network coming back. Throttled because those events cluster: `handleResume` fires on every
	 * visibilitychange and every `online` event, and a reconnect dispatches reconcile again from
	 * `onopen`. Leading edge keeps the first sync immediate; the trailing edge catches whatever
	 * changed during the burst.
	 */
	reconcile$ = createEffect(() =>
		this.actions.pipe(
			ofType(MessagingActions.message.reconcile),
			throttleTime(RECONCILE_THROTTLE_MS, asyncScheduler, {
				leading: true,
				trailing: true
			}),
			switchMap(() =>
				this.messagingService.conversations().pipe(
					map((conversations) =>
						MessagingActions.conversation.getSuccess({
							conversations: conversations || []
						})
					),
					catchError((error) =>
						of(MessagingActions.conversation.getFail(new FailState(error)))
					)
				)
			)
		)
	);

	constructor(
		private actions: Actions,
		private messagingService: MessagingService,
		private messagingV2Service: MessagingV2Service
	) {}
}
